import type { Answers, FormStatus } from '@/pages/home/types';
import type { Evaluation } from '@/pages/home/utils/evaluate';
import { menstrualLabel } from '@/pages/home/utils/evaluate';
import SectionCard from '@/pages/home/components/SectionCard';

interface ResultStepProps {
  answers: Answers;
  evaluation: Evaluation;
  status: FormStatus;
  onRestart: () => void;
}

const readText = (answers: Answers, key: string) => {
  const value = answers[key];
  return typeof value === 'string' && value ? value : '未填写';
};

export default function ResultStep({ answers, evaluation, status, onRestart }: ResultStepProps) {
  const percent = Math.min(100, Math.round((evaluation.totalScore / 36) * 100));

  return (
    <>
      <section className="bg-background-50 rounded-xl border border-secondary-200/70 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-semibold shrink-0">
            1
          </span>
          <h2 className="text-base font-semibold text-primary-600">你的评估结果</h2>
        </div>

        <div className="bg-background-100 rounded-xl px-4 py-5 text-center">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-semibold ${evaluation.badgeClass}`}
          >
            <i className="ri-heart-pulse-line"></i>
            {evaluation.levelTitle}
          </span>
          <p className="mt-3 text-4xl font-heading font-semibold text-foreground-950">
            {evaluation.totalScore}
            <span className="text-base font-normal text-foreground-400 ml-1">/ 36 分</span>
          </p>
          <p className="mt-2 text-xs text-foreground-500 leading-relaxed px-2">
            {evaluation.levelDesc}
          </p>
          <div className="mt-4 w-full h-2 bg-secondary-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-700"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] text-foreground-400">
            <span>无明显症状</span>
            <span>症状偏重</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="bg-background-100 rounded-lg px-3 py-2.5">
            <p className="text-[10px] text-foreground-400 mb-1">已评项目</p>
            <p className="text-sm font-semibold text-foreground-800">{evaluation.ratedCount} / 12 项</p>
          </div>
          <div className="bg-background-100 rounded-lg px-3 py-2.5">
            <p className="text-[10px] text-foreground-400 mb-1">中度以上症状</p>
            <p className="text-sm font-semibold text-foreground-800">{evaluation.severeCount} 项</p>
          </div>
        </div>

        {evaluation.focus.length > 0 ? (
          <div className="mt-4">
            <p className="text-xs text-foreground-500 mb-2">最需要关注的信号</p>
            <div className="flex flex-wrap gap-2">
              {evaluation.focus.map((item) => (
                <span
                  key={item.label}
                  className="px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/70 text-xs text-primary-700"
                >
                  {item.label} · {item.level}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <SectionCard index={2} title="郡主给你的方向">
        <ul className="space-y-2.5">
          {evaluation.advice.map((line) => (
            <li key={line} className="flex gap-2 text-sm text-foreground-700 leading-relaxed">
              <i className="ri-checkbox-circle-fill text-primary-500 text-base shrink-0 mt-0.5"></i>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard index={3} title="你填写的信息">
        <div className="space-y-2 text-sm">
          {[
            { label: '年龄', value: readText(answers, 'age') },
            { label: '身高体重', value: readText(answers, 'body') },
            { label: '月经状态', value: menstrualLabel(answers) },
            { label: '激素检查', value: `FSH ${readText(answers, 'fsh')} / E2 ${readText(answers, 'e2')}` },
            { label: '既往病史', value: Array.isArray(answers.history) && answers.history.length ? answers.history.join('、') : '未填写' },
            { label: '联系方式', value: readText(answers, 'contact') },
          ].map((row) => (
            <div key={row.label} className="flex gap-3 border-b border-secondary-200/60 last:border-0 pb-2 last:pb-0">
              <span className="text-foreground-400 w-20 shrink-0">{row.label}</span>
              <span className="text-foreground-800 break-words">{row.value}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="bg-primary-50 rounded-xl border border-primary-200/60 px-4 py-3">
        <p className="text-xs text-primary-700 leading-relaxed flex gap-2">
          <i className="ri-information-line text-base shrink-0"></i>
          <span>
            这份结果是根据你的症状评分自动生成的参考方向,<strong className="font-semibold">不替代医疗诊断</strong>。
            提交后郡主会看到你的完整填写内容,再给你做针对性分析。
          </span>
        </p>
      </div>

      {status.state === 'success' ? (
        <div className="bg-background-50 rounded-xl border border-primary-300 px-4 py-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-primary-600 mb-1">
            <i className="ri-checkbox-circle-fill"></i>
            已经提交成功啦
          </p>
          <p className="text-xs text-foreground-500 leading-relaxed">
            {status.message || '郡主收到你的表了,会尽快回复你。也可以直接截图这一页发给我。'}
          </p>
        </div>
      ) : null}

      {status.state === 'error' && status.message ? (
        <div className="bg-background-50 rounded-xl border border-accent-300 px-4 py-3">
          <p className="text-xs text-accent-900 leading-relaxed flex gap-2">
            <i className="ri-error-warning-line text-base shrink-0"></i>
            <span>{status.message}</span>
          </p>
        </div>
      ) : null}

      <button
        type="button"
        onClick={onRestart}
        className="w-full py-3 rounded-xl text-sm font-medium text-foreground-500 bg-background-50 border border-secondary-200 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        重新填一份
      </button>
    </>
  );
}